<template>
  <div v-if="item.type === ITEM_TYPES.SEPARATOR" class="mb-8">
    <h3 v-linkify class="mb-0">
      {{ $sanitize(item.name, { allowedTags: ['a'] }) }}
    </h3>
    <p
      v-if="item.description"
      class="ma-0 mt-1 text-body-2 grey--text text--darken-2"
    >
      {{ item.description }}
    </p>
  </div>

  <div v-else>
    <div
      class="d-flex flex-column justify-center"
      :class="{ 'is-highlighted': pulse }"
      data-testid="checklistReplyItem-container-highlight"
    >
      <p
        v-linkify
        class="checklist-item-title mt-n7 mb-0 grey--text text--darken-4 body1"
        data-testid="checklistReplyItem-title"
        style="white-space: pre-line"
      >
        {{ $sanitize(item.name + isRequiredMark, { allowedTags: ['a'] }) }}
      </p>
      <p
        v-if="item.description"
        v-linkify
        class="ma-0 mt-1 pa-0 text-body-2 grey--text text--darken-2 mb-4"
      >
        {{ $sanitize(item.description, { allowedTags: ['a'] }) }}
      </p>

      <MediaContainer
        v-if="hasMedia"
        onlyThumbnails
        readOnly
        smallThumbnails
        :images="item.referenceMedia"
      />

      <template v-if="readOnly">
        <template v-if="item.type === ITEM_TYPES.ACCOUNT">
          <AccountForm
            class="mt-3"
            :response="item.response"
            :loading="loading"
            :rules="rules"
            :readOnly="readOnly"
            :accounts="item.resolvedAccounts || item.accounts || []"
            @change="handleChange"
          />
        </template>
        <template
          v-else-if="item.type === ITEM_TYPES.SIGNATURE && item.signature"
        >
          <SignatureForm
            class="mt-3"
            data-testid="checklistReplyItem-form-signatureReadonly"
            :account="item.signer"
            :loading="loading"
            :readOnly="true"
            :signature="signatureMedia"
          />
        </template>
        <template
          v-else-if="
            item.type !== ITEM_TYPES.SIGNATURE &&
            (hasResponse || hasResponseImage)
          "
        >
          <template v-if="hasMultipleResponses">
            <Response v-for="text in response" :key="text" :response="text" />
          </template>
          <Response v-else-if="response" :response="response" />

          <ImageResponse
            v-else-if="item.type === ITEM_TYPES.IMAGE"
            :response="item.responseImages"
          />
          <ChecklistFindingFooter
            v-if="hasFinding"
            :hasFinding="hasFinding"
            @openCreateFindingModal="$emit('openCreateFindingModal')"
            @viewFinding="$emit('viewFinding')"
          />
        </template>
        <template v-else>
          <div>
            <v-chip
              data-testid="checklistReplyItem-chip-noResponse"
              small
              disabled
              class="label mt-2"
              >{{ $t('checklist.no_response') }}</v-chip
            >
          </div>
        </template>

        <div v-if="showScore" class="d-flex align-end justify-end mt-3">
          <span
            class="font-weight-bold blue--text text--darken-1"
            style="font-size: 20px"
          >
            {{ localizedItemScorePoints }}
          </span>

          <span
            class="font-weight-medium font-size-13 blue-grey--text text--darken-1 ml-1 mb-1"
            style="text-transform: lowercase"
          >
            {{
              $t('routines.execution.total_points', { count: itemScorePoints })
            }}
          </span>
        </div>
      </template>

      <template v-if="!readOnly">
        <TextForm
          v-if="item.type === ITEM_TYPES.TEXT"
          class="mt-3"
          :response="item.response"
          :loading="loading"
          :rules="rules"
          @change="handleChange"
        />
        <SelectForm
          v-if="item.type === ITEM_TYPES.SELECT"
          :key="restoreKey"
          class="mt-3"
          :hasFinding="hasFinding"
          :response="item.response"
          :options="item.options"
          :loading="loading"
          :rules="rules"
          @change="handleChange"
          @handleWarningContinue="$emit('handleWarningContinue', $event)"
          @openCreateFindingModal="$emit('openCreateFindingModal', $event)"
        />
        <CheckboxesForm
          v-if="item.type === ITEM_TYPES.CHECKBOXES"
          :key="restoreKey"
          class="mt-3"
          :hasFinding="hasFinding"
          :response="item.response"
          :options="item.options"
          :loading="loading"
          :rules="rules"
          @change="handleChange"
          @handleWarningContinue="$emit('handleWarningContinue', $event)"
          @openCreateFindingModal="$emit('openCreateFindingModal', $event)"
        />
        <RadiosForm
          v-if="item.type === ITEM_TYPES.RADIOS"
          :key="restoreKey"
          class="mt-3"
          :hasFinding="hasFinding"
          :response="item.response"
          :options="item.options"
          :loading="loading"
          :rules="rules"
          :showRequiredFinding="false"
          @change="handleChange"
          @handleWarningContinue="$emit('handleWarningContinue', $event)"
          @openCreateFindingModal="$emit('openCreateFindingModal', $event)"
        />
        <NumberForm
          v-if="item.type === ITEM_TYPES.NUMBER"
          :key="restoreKey"
          class="mt-3"
          :loading="loading"
          :response="item.response"
          :rules="rules"
          @change="handleChange"
        />
        <DateForm
          v-if="item.type === ITEM_TYPES.DATE"
          class="mt-3"
          :loading="loading"
          :response="item.response"
          :rules="rules"
          :includeTime="item.includeTime"
          @change="handleChange"
        />
        <ScaleForm
          v-if="item.type === ITEM_TYPES.SCALE"
          :key="restoreKey"
          class="mt-3"
          :max="item.max"
          :min="item.min"
          :loading="loading"
          :response="item.response"
          :rules="rules"
          @change="handleChange"
        />
        <ImageForm
          v-if="item.type === ITEM_TYPES.IMAGE"
          class="mt-3"
          :response="item.responseImages"
          :loading="loading"
          :isImageValid="isImageTypeValid"
          @change="handleMediaChange"
        />
        <AccountForm
          v-if="item.type === ITEM_TYPES.ACCOUNT"
          class="mt-3"
          :response="item.response"
          :loading="loading"
          :rules="rules"
          :readOnly="readOnly"
          :accounts="item.resolvedAccounts || item.accounts || []"
          @change="handleChange"
        />
        <SignatureForm
          v-if="item.type === ITEM_TYPES.SIGNATURE"
          class="mt-3"
          data-testid="checklistReplyItem-form-signatureEditable"
          :account="item.signer"
          :loading="loading"
          :readOnly="readOnly"
          :rules="rules"
          :signature="signatureMedia"
          @open="handleOpenSignature"
          @delete="handleDeleteSignature"
        />

        <ChecklistFindingFooter
          v-if="requiresFinding && requestFinding"
          :hasFinding="hasFinding"
          @openCreateFindingModal="$emit('openCreateFindingModal')"
          @viewFinding="$emit('viewFinding')"
        />
      </template>
    </div>
    <v-divider
      v-if="item.type !== ITEM_TYPES.SEPARATOR && showDivider"
      class="my-6"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { utils } from 'frontend-js-shared'

import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import { formatLocalizedPoints } from '@/lib/formatLocalizedPoints'

import TextForm from './TextForm.vue'
import SelectForm from './SelectForm.vue'
import CheckboxesForm from './CheckboxesForm.vue'
import RadiosForm from './RadiosForm.vue'
import NumberForm from './NumberForm.vue'
import ScaleForm from './ScaleForm.vue'
import DateForm from './DateForm.vue'
import Response from './Response.vue'
import ImageForm from './ImageForm.vue'
import ImageResponse from './ImageResponse.vue'
import ChecklistFindingFooter from './ChecklistFindingFooter.vue'
import AccountForm from './AccountForm.vue'
import SignatureForm from './SignatureForm.vue'

const ITEM_TYPES = {
  ACCOUNT: 'accountSelection',
  CHECKBOXES: 'checkboxes',
  DATE: 'date',
  NUMBER: 'number',
  RADIOS: 'radios',
  SCALE: 'scale',
  SELECT: 'select',
  SEPARATOR: 'separator',
  SIGNATURE: 'signature',
  TEXT: 'text',
  IMAGE: 'images',
}

export default {
  name: 'Item',

  components: {
    MediaContainer,
    TextForm,
    SelectForm,
    CheckboxesForm,
    RadiosForm,
    NumberForm,
    ScaleForm,
    DateForm,
    Response,
    ImageForm,
    ImageResponse,
    ChecklistFindingFooter,
    AccountForm,
    SignatureForm,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },

    loading: Boolean,

    readOnly: Boolean,

    routineIsCompleted: Boolean,

    showDivider: {
      type: Boolean,
      default: true,
    },
    imageErrors: Array,
    requestFinding: Boolean,
    appearancePulseEnabled: {
      type: Boolean,
      default: false,
    },
    // Key for the stateful trigger forms; bump to re-render them from the
    // stored answers.
    restoreKey: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      ITEM_TYPES,
      pulse: false,
    }
  },

  created() {
    // Frozen at mount: the pulse must only ever play for elements that
    // appeared (i.e. mounted while the item was newly visible). Reactively
    // following the prop would add the class to already-visible items when
    // the editing gate flips on, falsely pulsing them.
    this.pulse =
      this.appearancePulseEnabled &&
      utils.checklists.hasVisibilityRule(this.item)
  },

  computed: {
    hasMedia() {
      return !_.isEmpty(this.item?.referenceMedia)
    },

    hasResponse() {
      return !_.isEmpty(this.response)
    },

    hasMultipleResponses() {
      return _.isArray(this.item?.response) && !_.isEmpty(this.item?.response)
    },

    hasResponseImage() {
      return (
        this.item.type === ITEM_TYPES.IMAGE &&
        !_.isEmpty(this.item.responseImages)
      )
    },

    response() {
      if (_.isNumber(this.item?.response)) return String(this.item.response)
      if (!this.item?.response) return ''

      switch (this.item.type) {
        case ITEM_TYPES.RADIOS:
        case ITEM_TYPES.SELECT:
          return this.getSelectedOptionText()
        case ITEM_TYPES.CHECKBOXES:
          return this.getMultipleOptionTexts()
        case ITEM_TYPES.DATE:
          return this.formatDateResponse()
        default:
          return this.item.response
      }
    },
    isImageTypeValid() {
      return !this.imageErrors.includes(this.item.id)
    },
    isRequiredMark() {
      return this.item.isRequired ? '*' : ''
    },

    rules() {
      return [
        value => {
          if (!this.item.isRequired) return true
          if (this.item.type === ITEM_TYPES.ACCOUNT) {
            return (
              (Array.isArray(value) && !_.isEmpty(value)) ||
              this.$t('validation.form.required_question')
            )
          }
          if (this.item.type === ITEM_TYPES.SIGNATURE) {
            return (
              Boolean(this.item?.signature) ||
              this.$t('validation.form.required_question')
            )
          }
          if (_.isNumber(value)) return true
          if (_.isEmpty(value))
            return this.$t('validation.form.required_question')
          return true
        },
      ]
    },

    hasFinding() {
      return !_.isEmpty(this.item.responseFindings)
    },

    signatureMedia() {
      if (!this.item?.signature) return null

      return this.item.signature.media || this.item.signature
    },

    itemScorePoints() {
      const points = this.item?.scoring?.points
      if (_.isNil(points) || points === '') return null

      const normalizedPoints = Number(points)
      return Number.isFinite(normalizedPoints) ? normalizedPoints : null
    },

    localizedItemScorePoints() {
      if (!_.isNumber(this.itemScorePoints)) {
        return null
      }

      return formatLocalizedPoints(this.itemScorePoints)
    },

    showScore() {
      return (
        this.readOnly &&
        this.routineIsCompleted &&
        _.isNumber(this.itemScorePoints)
      )
    },

    requiresFinding() {
      return this.item.options
        ?.filter(option => this.item.response?.includes(option.id))
        .some(option => option.requiresFinding)
    },
  },

  methods: {
    handleChange(value) {
      this.$emit('change', value)
    },

    handleOpenSignature() {
      this.$emit('openSignatureDialog')
    },

    handleDeleteSignature() {
      this.$emit('deleteSignature')
    },

    handleMediaChange(responseImages) {
      this.$emit('addImage', responseImages)
    },

    getSelectedOptionText() {
      const selectedOption = this.item?.options?.find?.(
        option => option.id === this.item?.response
      )
      return selectedOption.name ?? ''
    },

    getMultipleOptionTexts() {
      const selectedOptions = this.item?.options?.filter?.(option =>
        this.item.response.includes(option.id)
      )
      return selectedOptions.map(option => option.name)
    },

    formatDateResponse() {
      const m = moment(this.item.response)
      if (!m.isValid()) return this.item.response

      if (this.item.includeTime) {
        return m.format('LLL')
      }
      return m.format('LL')
    },
  },
}
</script>

<style lang="scss" scoped>
.is-highlighted {
  animation: checklist-highlight-pulse 1s linear 0s 1 normal none;
}

@keyframes checklist-highlight-pulse {
  from {
    background-color: $color-yellow-lighten2;
    box-shadow: 0px 0px 0px 15px $color-yellow-lighten2;
  }

  to {
    background-color: transparent;
  }
}
</style>
